import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAirAdditionalServiceOffer, removeAirAdditionalService, selectAirAdditionalService } from '../../../api/endpoints/air';
import type { AirBooking, FlightSegment, SeatMapSeatInfo, SelectedSpecialService } from '../../../api/types/air';
import type { ShoppingCart } from '../../../api/types/shoppingCart';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { AirlineLogo, airlineCodeOf, airlineNameOf } from '../../../components/AirlineLogo';

interface PickedSeat {
  rowNumber: string;
  seatInRow: string;
  seatNumber: string;
  chargeable: boolean;
  amount: number;
  currency: string;
}

interface ConfirmedSeat {
  seatNumber: string;
  amount: number;
  currency: string;
}

function seatKey(segmentIndex: number, passengerIndex: string): string {
  return `${segmentIndex}-${passengerIndex}`;
}

function segmentLabel(segment: FlightSegment): string {
  return [segment.departureAirportCode, segment.arrivalAirportCode].filter(Boolean).join(' → ') || 'Segment';
}

/** Bir koltuğun ücretli olup olmadığını ve varsa ücretini `Amenity[0].Fee`'den okur —
 *  legacy `renderSeatMap`'teki aynı öncelik sırası (ilk amenity kaydı). */
function seatFee(seat: SeatMapSeatInfo): { amount: number; currency: string } {
  const fee = seat.amenity?.[0]?.fee;
  return { amount: toAmount(fee?.amount), currency: fee?.currencyCode ?? 'TRY' };
}

function isChargeable(seat: SeatMapSeatInfo): boolean {
  const chargeableFeature = (seat.features ?? []).some((f) => f.extension === 'Chargable');
  return chargeableFeature || seatFee(seat).amount > 0;
}

function isExitRow(seat: SeatMapSeatInfo): boolean {
  return (seat.features ?? []).some((f) => f.extension === 'Emergency Exit');
}

/** API bazı sayısal alanları (ör. `Amenity[].Fee.Amount`, `AdditionalServicePricedItem.
 *  TotalAmount`) JSON number değil, rakam dizesi ("50") olarak gönderiyor — confirmed
 *  runtime'da (`.toFixed is not a function`). Her yerde göstermeden önce buradan geçirip
 *  say bir sayıya zorluyoruz. */
function toAmount(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Compact "50 TRY" style label shown directly on chargeable seat cells — legacy
 *  `renderSeatMap`'in koltuk hücresine eklediği `<small>amount currency</small>`'in
 *  karşılığı, bizim gridimizde de aynı şekilde koltuğun altında görünüyor. */
function formatSeatPrice(rawAmount: number, currency: string): string {
  const amount = toAmount(rawAmount);
  const formatted = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
  return `${formatted} ${currency}`;
}

/**
 * Koltuk seçimi — sadece THYNDC (Türk Hava Yolları NDC) rezervasyonları için, Ödeme
 * adımından hemen önce gösterilen flow adımı. THYNDC dışındaki sağlayıcılarda bu ekran
 * bilgilendirme mesajıyla birlikte doğrudan geçilebilir.
 *
 * Legacy `AdditionalServicesController`'ın (MVC) `GetThyNdcSeatMap`/`PriceThyNdcSeat`/
 * `SelectThyNdcSeat`/`RemoveThyNdcSeat` action'larının kaynağı incelendi — hepsi aslında
 * doğrudan `Air/GetAdditionalServiceOffer` (koltuk haritası için `intention` boş,
 * fiyatlandırma için `intention: 'PRICING'`), `Air/SelectAdditionalService` ve
 * `Air/RemoveAdditionalService`'i sarmalıyor. Bu backend'de o MVC wrapper route'ları
 * (`/AdditionalServices/...`) yok — bu yüzden bu ekran o üç Web API rotasını doğrudan
 * çağırıyor, aynı request/response eşlemesiyle (bkz. api/types/air.ts'teki
 * `SeatMapResponse`/`AdditionalServicePricedOffer`/`SelectedSpecialService`).
 */
export function AirSeatSelectionStep({
  shoppingCart,
  loading,
  onContinue,
  onShoppingCartChange,
}: {
  shoppingCart: ShoppingCart | null | undefined;
  loading: boolean;
  onContinue: () => void;
  /** Koltuk seçimi/kaldırma sonrası güncellenen sepeti (yeni `remainingTotal` dahil)
   *  flow state'ine geri yazmak için — bu adım kendi API çağrılarını yapıyor,
   *  `flow.runStep` üzerinden değil. */
  onShoppingCartChange: (cart: ShoppingCart) => void;
}) {
  const firstBooking = (shoppingCart?.airBookings as AirBooking[] | undefined)?.[0];
  const isThyNdc = firstBooking?.provider === 'THYNDC';
  const segments = firstBooking?.flightSegments ?? [];
  const passengers = shoppingCart?.passengers ?? [];

  const [confirmedSeats, setConfirmedSeats] = useState<Record<string, ConfirmedSeat>>({});
  const [modalSegmentIndex, setModalSegmentIndex] = useState<number | null>(null);
  const [modalPassengerIndex, setModalPassengerIndex] = useState<string | null>(null);
  const [pickedSeat, setPickedSeat] = useState<PickedSeat | null>(null);

  const seatMapMutation = useMutation({
    mutationFn: (vars: { segmentIndex: number; passengerIndex: string }) =>
      getAirAdditionalServiceOffer({
        addedAdditionalServices: [
          {
            ssrCode: 'SEAT',
            shoppingCartId: shoppingCart?.id,
            segmentIndex: String(vars.segmentIndex),
            passengerIndex: vars.passengerIndex,
            quantitiy: 1,
          },
        ],
      }),
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!pickedSeat || modalSegmentIndex === null || !modalPassengerIndex || !shoppingCart?.id) {
        throw new Error('Koltuk seçilmedi.');
      }
      const priceRes = await getAirAdditionalServiceOffer({
        intention: 'PRICING',
        addedAdditionalServices: [
          {
            ssrCode: 'SEAT',
            shoppingCartId: shoppingCart.id,
            segmentIndex: String(modalSegmentIndex),
            passengerIndex: modalPassengerIndex,
            quantitiy: 1,
            selectedSeats: [
              {
                flightRefNumberRPHList: String(modalSegmentIndex),
                travelerRefNumberRPHList: modalPassengerIndex,
                rowNumber: pickedSeat.rowNumber,
                seatInRow: pickedSeat.seatInRow,
                chargable: pickedSeat.chargeable,
              },
            ],
          },
        ],
      });

      const items = priceRes.additionalServicePricedOffer?.additionalServicePricedItems ?? [];
      if (!items.length) throw new Error('Koltuk fiyatlandırılamadı.');

      const offerId = priceRes.additionalServicePricedOffer?.offerId;
      const selectedSpecialServices: SelectedSpecialService[] = items.map((item) => ({
        ancillaryItemType: item.rfisc,
        ancillaryServiceAmount: toAmount(item.quantity),
        ancillaryServiceUnit: item.serviceUnit,
        isIssued: false,
        offerId,
        offerItemId: item.offerItemId,
        passengerIndex: modalPassengerIndex,
        paymentAmount: toAmount(item.totalAmount),
        paymentAmountTRY: toAmount(item.rawTotalAmount),
        requestedAt: new Date().toISOString(),
        segmentIndex: item.segmentRefs,
        serviceId: item.serviceId,
        ssrCode: item.category,
        speqCode: item.speqName,
      }));
      const totalAmount = items.reduce((sum, item) => sum + toAmount(item.totalAmount), 0);

      const selectRes = await selectAirAdditionalService({
        shoppingCartId: shoppingCart.id,
        selectedSpecialServices,
      });

      return { totalAmount, shoppingCart: selectRes.shoppingCart };
    },
    onSuccess: ({ totalAmount, shoppingCart: updatedCart }) => {
      if (modalSegmentIndex === null || !modalPassengerIndex || !pickedSeat) return;
      setConfirmedSeats((prev) => ({
        ...prev,
        [seatKey(modalSegmentIndex, modalPassengerIndex)]: {
          seatNumber: pickedSeat.seatNumber,
          amount: totalAmount,
          currency: pickedSeat.currency,
        },
      }));
      if (updatedCart) onShoppingCartChange(updatedCart);
      setModalSegmentIndex(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (segmentIndex: number) =>
      removeAirAdditionalService({ shoppingCartId: shoppingCart?.id, segmentIndex }),
    onSuccess: (res, segmentIndex) => {
      setConfirmedSeats((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${segmentIndex}-`)) delete next[key];
        });
        return next;
      });
      if (res.shoppingCart) onShoppingCartChange(res.shoppingCart);
    },
  });

  if (!isThyNdc) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Bu rezervasyon THYNDC (Türk Hava Yolları NDC) üzerinden değil, koltuk seçimi bu sağlayıcı için
          desteklenmiyor.
        </Typography>
        <Box>
          <Button variant="contained" disabled={loading} onClick={onContinue}>
            Devam Et
          </Button>
        </Box>
      </Box>
    );
  }

  const openSeatMap = (segmentIndex: number, passengerIndex: string) => {
    setModalSegmentIndex(segmentIndex);
    setModalPassengerIndex(passengerIndex);
    setPickedSeat(null);
    seatMapMutation.mutate({ segmentIndex, passengerIndex });
  };

  const seatsTakenByOthers = (segmentIndex: number, excludePassengerIndex: string) => {
    const taken = new Set<string>();
    Object.entries(confirmedSeats).forEach(([key, val]) => {
      const [seg, pax] = key.split('-');
      if (Number(seg) === segmentIndex && pax !== excludePassengerIndex) taken.add(val.seatNumber);
    });
    return taken;
  };

  const seatMap = seatMapMutation.data?.seatMapResponse;
  const rows = seatMap?.seatMapDetails?.cabinClass?.rowInfo ?? [];
  const takenByOthers =
    modalSegmentIndex !== null && modalPassengerIndex ? seatsTakenByOthers(modalSegmentIndex, modalPassengerIndex) : new Set<string>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Bu rezervasyon THYNDC üzerinden yapıldı — ödemeden önce her yolcu için koltuk seçebilirsiniz.
      </Typography>

      {segments.map((segment, segIdx) => {
        const segmentIndex = segment.sequenceNo ?? segIdx + 1;
        return (
          <Card key={segment.productId ?? segmentIndex} variant="outlined">
            <CardContent>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <AirlineLogo size={16} code={airlineCodeOf(segment)} name={airlineNameOf(segment)} />
                  <Typography variant="subtitle2">
                    {segmentLabel(segment)} {segment.flightNumber ? `· ${segment.flightNumber}` : ''}
                  </Typography>
                </Stack>
                {Object.keys(confirmedSeats).some((k) => k.startsWith(`${segmentIndex}-`)) && (
                  <Button
                    size="small"
                    color="error"
                    disabled={removeMutation.isPending}
                    onClick={() => removeMutation.mutate(segmentIndex)}
                  >
                    {removeMutation.isPending ? 'Kaldırılıyor…' : 'Tüm Koltukları Kaldır'}
                  </Button>
                )}
              </Stack>
              <Stack spacing={1}>
                {passengers.map((passenger, paxIdx) => {
                  const passengerIndex = String(paxIdx + 1);
                  const confirmed = confirmedSeats[seatKey(segmentIndex, passengerIndex)];
                  const name = [passenger.firstName, passenger.lastName].filter(Boolean).join(' ') || `Yolcu ${passengerIndex}`;
                  return (
                    <Stack
                      key={passengerIndex}
                      direction="row"
                      sx={{ alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2">{name}</Typography>
                      {confirmed ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          color="success"
                          variant="outlined"
                          label={`${confirmed.seatNumber}${toAmount(confirmed.amount) > 0 ? ` (${formatSeatPrice(confirmed.amount, confirmed.currency)})` : ' (Ücretsiz)'}`}
                          onDelete={() => removeMutation.mutate(segmentIndex)}
                        />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => openSeatMap(segmentIndex, passengerIndex)}>
                          Koltuk Seç
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={modalSegmentIndex !== null} onClose={() => setModalSegmentIndex(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Koltuk Haritası
          <IconButton size="small" onClick={() => setModalSegmentIndex(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {seatMapMutation.isPending && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          <ApiErrorAlert error={seatMapMutation.error} sx={{ mb: 2 }} />
          {seatMapMutation.isSuccess && rows.length === 0 && (
            <Alert severity="warning">Koltuk haritası verisi bulunamadı.</Alert>
          )}
          {rows.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Stack spacing={0.5}>
                {rows.map((row, rowIdx) => (
                  <Stack key={row.rowNumber ?? rowIdx} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ width: 24, textAlign: 'right' }}>
                      {row.rowNumber}
                    </Typography>
                    {(row.seatInfo ?? []).map((seat, seatIdx) => {
                      if (seat.availability !== 'SeatAvailable' && seat.availability !== 'SeatReserved') {
                        return <Box key={seatIdx} sx={{ width: 32 }} />;
                      }
                      const seatNumber = seat.summary?.seatNumber ?? '';
                      const seatInRow = row.rowNumber && seatNumber.startsWith(row.rowNumber)
                        ? seatNumber.slice(row.rowNumber.length)
                        : seatNumber;
                      const takenByOther = takenByOthers.has(seatNumber);
                      const occupied = takenByOther || seat.availability === 'SeatReserved';
                      const chargeable = isChargeable(seat);
                      const { amount, currency } = seatFee(seat);
                      const selected = pickedSeat?.seatNumber === seatNumber;

                      return (
                        <Box key={seatIdx} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            onClick={() => {
                              if (occupied) return;
                              setPickedSeat({
                                rowNumber: row.rowNumber ?? '',
                                seatInRow,
                                seatNumber,
                                chargeable,
                                amount,
                                currency,
                              });
                            }}
                            title={[
                              ...(seat.features ?? []).map((f) => f.extension).filter(Boolean),
                              takenByOther ? 'Başka bir yolcu tarafından seçildi' : null,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                            sx={{
                              width: 40,
                              height: chargeable && amount > 0 ? 40 : 32,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              fontSize: 11,
                              lineHeight: 1.1,
                              cursor: occupied ? 'not-allowed' : 'pointer',
                              border: '1px solid',
                              borderColor: isExitRow(seat) ? 'error.main' : 'divider',
                              bgcolor: occupied
                                ? 'action.disabledBackground'
                                : selected
                                  ? 'primary.main'
                                  : chargeable
                                    ? 'warning.light'
                                    : 'success.light',
                              color: selected ? 'primary.contrastText' : 'text.primary',
                              opacity: occupied ? 0.5 : 1,
                            }}
                          >
                            <Box component="span">{seatInRow}</Box>
                            {chargeable && amount > 0 && (
                              <Box component="span" sx={{ fontSize: 9, fontWeight: 600 }}>
                                {formatSeatPrice(amount, currency)}
                              </Box>
                            )}
                          </Box>
                          {seatInRow === 'C' && <Box sx={{ width: 16 }} />}
                        </Box>
                      );
                    })}
                  </Stack>
                ))}
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Chip size="small" label="Ücretsiz" sx={{ bgcolor: 'success.light' }} />
                <Chip size="small" label="Ücretli" sx={{ bgcolor: 'warning.light' }} />
                <Chip size="small" label="Dolu" sx={{ bgcolor: 'action.disabledBackground' }} />
              </Stack>
            </Box>
          )}
          <ApiErrorAlert error={confirmMutation.error} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Typography variant="body2">
            {pickedSeat
              ? `Seçilen koltuk: ${pickedSeat.seatNumber} — ${
                  pickedSeat.chargeable && pickedSeat.amount > 0
                    ? formatSeatPrice(pickedSeat.amount, pickedSeat.currency)
                    : 'Ücretsiz'
                }`
              : 'Bir koltuk seçin.'}
          </Typography>
          <Box>
            <Button onClick={() => setModalSegmentIndex(null)}>Vazgeç</Button>
            <Button
              variant="contained"
              disabled={!pickedSeat || confirmMutation.isPending}
              onClick={() => confirmMutation.mutate()}
              sx={{ ml: 1 }}
            >
              {confirmMutation.isPending ? 'İşleniyor…' : 'Koltuğu Onayla'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Box>
        <Button variant="contained" disabled={loading} onClick={onContinue}>
          Devam Et (Ödemeye Geç)
        </Button>
      </Box>
    </Box>
  );
}
